import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { TableCellProps } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow, { TableRowProps } from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import {
  AddOnFuelSurchargeRate_Read,
  AddOnRateType,
  RateOwnerType,
} from '@treadinc/horizon-api-spec';
import { Dayjs } from 'dayjs';
import { t } from 'i18next';
import { capitalize, isNil } from 'lodash-es';
import { useEffect, useState } from 'react';

import { PseudoLink } from '~components/Helpers/PseudoLink';
import useAddOns from '~hooks/useAddOns/useAddOns';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import { rateTypeTextByAddOnRateType } from '~pages/Settings/RatesManagement/AddOnCharges/addOnChargesDataGridColumnsDefinition';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPercentage } from '~utils/utilFunctions';

import { EstimateCard } from '../shared-components';
import { AddOnAssignmentSchema } from './AutomaticAddOns';

const truncateWithEllipsis = (text: string, maxLength: number): string => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
};

const TableRowStyled = styled(TableRow)<TableRowProps>(({ theme }) => ({
  '&:not(.MuiTableBody-root tr:last-child)': {
    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
  },
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
}));

const TableCellStyled = styled(TableCell, {
  shouldForwardProp: (propName) => propName !== 'isHeader',
})<TableCellProps & { isHeader?: boolean }>(({ isHeader, theme }) => ({
  borderBottom: 0,
  color: theme.brandV2.colors.treadBlack,
  fontSize: isHeader ? '11px' : '12px',
  fontWeight: isHeader ? 600 : 400,
  padding: theme.spacing(0.5),
  flex: 1,
}));

const FuelSurchargeRateCell = ({
  addOnId,
  orderDate,
}: {
  addOnId?: string;
  orderDate?: string | Dayjs;
}) => {
  const { getFuelSurchargeRate } = useAddOns();
  const { currencyFormatter } = useCompanyCurrency();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [rate, setRate] = useState<AddOnFuelSurchargeRate_Read | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!companyId || !addOnId) return;
    const controller = new AbortController();
    const fetchRate = async () => {
      setIsLoading(true);
      const dateStr = orderDate
        ? typeof orderDate === 'string'
          ? orderDate
          : orderDate.format('YYYY-MM-DD')
        : undefined;
      try {
        const result = await getFuelSurchargeRate(
          companyId,
          addOnId,
          dateStr,
          controller.signal,
        );
        if (controller.signal.aborted) return;

        setRate(result);
        setIsLoading(false);
      } catch (error) {
        if (controller.signal.aborted) return;
        throw error;
      }
    };
    void fetchRate();
    return () => controller.abort();
  }, [companyId, addOnId, orderDate]);

  if (isLoading) {
    return <CircularProgress size={12} />;
  }
  if (!isNil(rate?.surcharge_percentage)) {
    return <>{formatPercentage(rate.surcharge_percentage)}</>;
  }
  if (!isNil(rate?.surcharge)) {
    return <>{currencyFormatter(Number(rate.surcharge))}</>;
  }
  return <>-</>;
};

export default function AddOnAssignmentCardV2({
  addOnAssignments,
  rateOwnerType,
  handleOpenAddOnDialog,
  disableTargetRateControls,
  orderDate,
}: {
  addOnAssignments: AddOnAssignmentSchema[];
  rateOwnerType?: RateOwnerType;
  handleOpenAddOnDialog?: () => void;
  disableTargetRateControls?: boolean;
  orderDate?: string | Dayjs;
}) {
  const { currencyFormatter } = useCompanyCurrency();
  const displayRateType = (rateType: AddOnRateType | null) =>
    rateType ? rateTypeTextByAddOnRateType[rateType] : '';

  const filteredAddOnAssignments = addOnAssignments.filter(
    (addOnAssignment) => !addOnAssignment.isDeleted,
  );

  if (filteredAddOnAssignments.length === 0) {
    return null;
  }

  const renderCostCell = (addOnAssignment: AddOnAssignmentSchema) =>
    !isNil(addOnAssignment.addOn?.costRateValue) &&
    addOnAssignment.addOn?.rateType !== AddOnRateType.FUEL_SURCHARGE
      ? addOnAssignment.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
        ? formatPercentage(addOnAssignment.addOn?.costRateValue)
        : currencyFormatter(addOnAssignment.addOn?.costRateValue ?? 0)
      : '-';

  const renderInvoiceRateCell = (addOnAssignment: AddOnAssignmentSchema) =>
    addOnAssignment.addOn?.rateType !== AddOnRateType.FUEL_SURCHARGE ? (
      addOnAssignment.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL ? (
        formatPercentage(addOnAssignment.addOn?.rateValue)
      ) : (
        currencyFormatter(addOnAssignment.addOn?.rateValue ?? 0)
      )
    ) : (
      <FuelSurchargeRateCell
        addOnId={addOnAssignment.addOn?.addOnId ?? addOnAssignment.addOn?.id ?? undefined}
        orderDate={orderDate}
      />
    );

  const renderQuantityCell = (addOnAssignment: AddOnAssignmentSchema) =>
    addOnAssignment.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
    addOnAssignment.addOn?.rateType === AddOnRateType.PER_LOAD
      ? addOnAssignment.addOn?.quantity
      : '-';

  return (
    <>
      <EstimateCard title={''} variant="light">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            justifyContent: 'space-between',
          }}
        >
          <Typography
            sx={{
              pl: 0.5,
              fontWeight: 800,
              fontSize: 13,
            }}
          >
            {rateOwnerType
              ? `${capitalize(rateOwnerType)} ${t('common.add_ons')}`
              : t('common.add_ons')}
          </Typography>
          {!disableTargetRateControls && handleOpenAddOnDialog && (
            <PseudoLink title={t('actions.edit')} action={handleOpenAddOnDialog} />
          )}
        </Box>
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRowStyled>
                <TableCellStyled isHeader sx={{ flex: 1.5 }}>
                  {t('common.description')}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t('administration.rates.add_ons.cost_rate')}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t('administration.rates.invoice_rate')}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t('administration.rates.automatic_add_ons.quantity')}
                </TableCellStyled>
                <TableCellStyled isHeader>
                  {t('administration.rates.rate_type')}
                </TableCellStyled>
              </TableRowStyled>
            </TableHead>

            <TableBody>
              {filteredAddOnAssignments.map((addOnAssignment, index) => (
                <TableRowStyled key={addOnAssignment.addOn?.id ?? index}>
                  <TableCellStyled sx={{ flex: 1.5 }}>
                    {truncateWithEllipsis(addOnAssignment.addOn?.name ?? '', 35)}
                  </TableCellStyled>
                  <TableCellStyled>{renderCostCell(addOnAssignment)}</TableCellStyled>
                  <TableCellStyled>
                    {renderInvoiceRateCell(addOnAssignment)}
                  </TableCellStyled>
                  <TableCellStyled>{renderQuantityCell(addOnAssignment)}</TableCellStyled>
                  <TableCellStyled>
                    {displayRateType(
                      (addOnAssignment.addOn?.rateType as AddOnRateType) ?? null,
                    )}
                  </TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </EstimateCard>
    </>
  );
}
